// Maps between the Clever config form and sync-api v2 (SM-API), the counterpart
// to the Google/Azure mapper. Clever is a device-level singleton: reads resolve
// the one CLEVER node; writes create it if absent, else update.
//
// The component's `toFormState`/`toConfigInput` already speak an intermediate
// "config" shape (matchUsersBy* booleans, string parent types, `syncAttributes`),
// so rather than duplicate that enum/merge logic we translate the sync-api v2
// node into that shape (read) and translate the validated intermediate back into
// the v2 `clever` block (write). All v2 field-name/enum knowledge lives here.
// Only type-only imports are taken from CleverConfiguration (no runtime cycle).
import {
  CleverMatchUsersBy,
  CleverParentType,
  CleverProviderConfig,
  CleverSyncConfigInput,
  CredentialState,
  createSyncConfiguration,
  MergeConfigInput,
  resolveSyncIdByType,
  SyncConfigurationNode,
  SyncType,
  updateSyncConfiguration,
} from "../../../utils/api/SyncApi";
import type { ConfigInput, SyncApiConfigResponse, SyncAttributeMapping } from "./CleverConfiguration";
import { YearAttributeEnum } from "../sis-integration/SisIntegration";

// v2 CleverParentType <-> the form's ParentType string values ("Parent/Guardian", …).
const PARENT_TYPE_V2_TO_FORM: Record<CleverParentType, string> = {
  PARENT_GUARDIAN: "Parent/Guardian",
  FAMILY: "Family",
  EMERGENCY: "Emergency",
  OTHER: "Other",
};
const PARENT_TYPE_FORM_TO_V2: Record<string, CleverParentType> = {
  "Parent/Guardian": "PARENT_GUARDIAN",
  Family: "FAMILY",
  Emergency: "EMERGENCY",
  Other: "OTHER",
};

/**
 * Adapt a sync-api v2 node into the `SyncApiConfigResponse` shape the component's
 * `toFormState` consumes. Translates the v2 `matchUsersBy` enum into the form's
 * two booleans, v2 parent-type enums into the form's strings, and `yearAttributes`
 * into the form's `syncAttributes.years`. `clever_client_id` is unused on the v2
 * OAuth path (sync-api holds it), so "".
 */
export const cleverNodeToResponse = (node?: SyncConfigurationNode): SyncApiConfigResponse => {
  if (!node) {
    return { configuration: null, clever_client_id: "" };
  }

  const config = node.config as CleverProviderConfig | undefined;
  const merge = node.merge;

  return {
    clever_client_id: "",
    configuration: {
      id: node.id,
      config: {
        enabled: node.enabled,
        districtID: config?.cleverDistrictID ?? "",
        matchUsersByName: config?.matchUsersBy === "NAME_OR_EMAIL",
        matchUsersByUsername: config?.matchUsersBy === "USERNAME_OR_EMAIL",
        forceUniqueName: config?.forceUniqueClassroomNames ?? false,
        syncParents: config?.syncParents ?? false,
        syncClassroomData: config?.syncClassroomData ?? false,
        syncParentTypes: (config?.cleverSyncParentTypes ?? []).map((t) => PARENT_TYPE_V2_TO_FORM[t]),
        // Node merge carries no `__typename`; synthesise it so `toFormState`'s
        // discriminated-union checks keep working.
        merge: merge
          ? {
              priority: merge.priority,
              attachOnly: merge.attachOnly,
              method:
                merge.method?.expression !== undefined
                  ? { __typename: "ComputedMergeMethod", expression: merge.method.expression }
                  : { __typename: "EmailMergeMethod", stripped: merge.method?.stripped ?? false },
            }
          : undefined,
        // v2 `yearAttributes` -> the form's `syncAttributes.years`. Year enum
        // values are identical (IT/PR/…/OTHER); only the field names differ.
        syncAttributes: config?.yearAttributes
          ? {
              years: {
                enabled: config.yearAttributes.enabled,
                mappings: config.yearAttributes.mappings.map(
                  (m): SyncAttributeMapping => ({ customerValue: m.customerValue, enumValue: m.enumValue as YearAttributeEnum })
                ),
              },
            }
          : undefined,
      },
    },
  };
};

/** True when the Clever sync's stored credential is set (i.e. it has been linked). */
export const isCleverCredentialLinked = (node?: SyncConfigurationNode): boolean => {
  const config = node?.config as CleverProviderConfig | undefined;
  return config?.credential?.state === CredentialState.Set;
};

const toMatchUsersBy = (input: ConfigInput): CleverMatchUsersBy => {
  if (input.matchUsersByName) return "NAME_OR_EMAIL";
  if (input.matchUsersByUsername) return "USERNAME_OR_EMAIL";
  return "EMAIL_ONLY";
};

/** Create (if none) or update the singleton Clever sync from validated form config. */
export const saveCleverSync = async (input: ConfigInput, id?: string): Promise<void> => {
  const clever: CleverSyncConfigInput = {
    matchUsersBy: toMatchUsersBy(input),
    syncClassroomData: input.syncClassroomData,
    forceUniqueClassroomNames: input.forceUniqueName,
    syncParents: input.syncParents,
    syncParentTypes: (input.syncParentTypes ?? []).map((t) => PARENT_TYPE_FORM_TO_V2[t]).filter(Boolean),
    yearAttributes: input.syncAttributes
      ? {
          enabled: input.syncAttributes.years.enabled,
          mappings: input.syncAttributes.years.mappings.map((m) => ({ enumValue: m.enumValue, customerValue: m.customerValue })),
        }
      : undefined,
  };

  // `enabled` and `merge` are top-level on the SM-API create/update inputs, not
  // inside the provider block. `districtID` is omitted (owned by the OAuth flow).
  const merge: MergeConfigInput = {
    priority: input.merge?.priority ?? 10,
    attachOnly: input.merge?.attachOnly ?? false,
    method: input.merge?.method ?? { email: { stripped: false } },
  };

  const syncId = id ?? (await resolveSyncIdByType(SyncType.Clever));
  if (syncId) {
    await updateSyncConfiguration(syncId, { enabled: input.enabled, merge, clever });
  } else {
    await createSyncConfiguration({
      syncType: SyncType.Clever,
      name: "Clever",
      enabled: input.enabled,
      merge,
      clever,
    });
  }
};
